// Per-tenant clinical validation range configuration — SPEC §8.
// Compliance tenants always use DRS global bounds (not editable here).

import { useState, useEffect } from "react";
import type { JSX } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { AdminNav } from "../components/AdminNav.js";
import { adminFetch } from "../lib/api.js";

const VITAL_KEYS = [
  { key: "bp_systolic",        label: "Systolic BP",       unit: "mmHg" },
  { key: "bp_diastolic",       label: "Diastolic BP",      unit: "mmHg" },
  { key: "heart_rate",         label: "Heart Rate",        unit: "bpm"  },
  { key: "spo2_percent",       label: "SpO2",              unit: "%"    },
  { key: "temperature_celsius",label: "Temperature",       unit: "°C"   },
  { key: "weight_kg",          label: "Weight",            unit: "kg"   },
  { key: "blood_glucose",      label: "Blood Glucose",     unit: "mmol/L" },
  { key: "respiratory_rate",   label: "Respiratory Rate",  unit: "breaths/min" },
] as const;

type VitalKey = typeof VITAL_KEYS[number]["key"];

interface VitalRange { min: number; max: number; critical_low?: number; critical_high?: number; }
type Ranges = Partial<Record<VitalKey, VitalRange>>;

function RangeRow({
  label, unit, vkey, range, onChange,
}: {
  label: string; unit: string; vkey: VitalKey;
  range: VitalRange | undefined;
  onChange: (key: VitalKey, field: keyof VitalRange, value: string) => void;
}): JSX.Element {
  const val = (f: keyof VitalRange): string => String(range?.[f] ?? "");

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-3 pr-4 font-medium text-slate-700 whitespace-nowrap">
        {label} <span className="text-xs text-slate-400">({unit})</span>
      </td>
      {(["min", "max", "critical_low", "critical_high"] as const).map((f) => (
        <td key={f} className="py-2 pr-3">
          <input
            type="number"
            step="any"
            value={val(f)}
            onChange={(e) => onChange(vkey, f, e.target.value)}
            placeholder={f.replace("_", " ")}
            className="w-28 rounded border border-slate-300 px-2 py-1 text-sm focus:border-teal-500 focus:outline-none"
          />
        </td>
      ))}
    </tr>
  );
}

interface RangesFormProps {
  ranges: Ranges | null;
  isPending: boolean;
  isError: boolean;
  saved: boolean;
  onChange: (key: VitalKey, field: keyof VitalRange, value: string) => void;
  onSave: (e: React.FormEvent) => void;
}

function ValidationRangesForm({ ranges, isPending, isError, saved, onChange, onSave }: RangesFormProps): JSX.Element {
  return (
    <form onSubmit={onSave}>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="py-3 pr-4 pl-4">Vital</th>
              <th className="py-3 pr-3">Min</th>
              <th className="py-3 pr-3">Max</th>
              <th className="py-3 pr-3">Critical Low</th>
              <th className="py-3 pr-3">Critical High</th>
            </tr>
          </thead>
          <tbody className="pl-4">
            {VITAL_KEYS.map(({ key, label, unit }) => (
              <RangeRow key={key} vkey={key} label={label} unit={unit} range={(ranges ?? {})[key]} onChange={onChange} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button type="submit" disabled={isPending} className="rounded-lg bg-teal-600 px-5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">
          {isPending ? "Saving…" : "Save Ranges"}
        </button>
        {saved   && <span className="text-sm text-emerald-600">Saved.</span>}
        {isError && <span className="text-sm text-red-500">Save failed.</span>}
      </div>
    </form>
  );
}

export function ValidationRangesPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [ranges, setRanges] = useState<Ranges | null>(null);
  const [saved, setSaved] = useState(false);

  const { isLoading, isError, data: queryData } = useQuery<{ validation_ranges: Ranges }>({
    queryKey: ["admin-validation-ranges", id],
    queryFn: () => adminFetch(`/admin/tenants/${id}/validation-ranges`),
  });

  useEffect(() => { if (queryData && !ranges) setRanges(queryData.validation_ranges); }, [queryData]);

  const mutation = useMutation({
    mutationFn: (body: Ranges) => adminFetch(`/admin/tenants/${id}/validation-ranges`, { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-validation-ranges", id] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  function handleChange(key: VitalKey, field: keyof VitalRange, value: string): void {
    setRanges((prev) => {
      const current = prev ?? {};
      const entry = { ...(current[key] ?? { min: 0, max: 0 }) };
      const parsed = parseFloat(value);
      if (value === "" || isNaN(parsed)) { const updated = { ...entry }; delete updated[field]; return { ...current, [key]: updated }; }
      return { ...current, [key]: { ...entry, [field]: parsed } };
    });
  }

  function handleSave(e: React.FormEvent): void { e.preventDefault(); mutation.mutate(ranges ?? {}); }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />
      <main className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-6 flex items-center gap-3">
          <Link to="/tenants" className="text-sm text-slate-500 hover:text-teal-600">← Tenants</Link>
          <h1 className="text-xl font-semibold text-slate-800">Validation Ranges</h1>
          <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-500">Tenant {id}</span>
        </div>
        <p className="mb-6 text-sm text-slate-500">
          Override global clinical bounds for this tenant. Compliance-mode tenants always use DRS bounds — ranges set here are ignored for them.
        </p>
        {isLoading && <p className="text-slate-400">Loading…</p>}
        {isError   && <p className="text-red-500">Failed to load ranges.</p>}
        {!isLoading && <ValidationRangesForm ranges={ranges} isPending={mutation.isPending} isError={mutation.isError} saved={saved} onChange={handleChange} onSave={handleSave} />}
      </main>
    </div>
  );
}
